import { Col, Form, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { Input } from "../../components/Input/input";
import { ProfilePhoto } from "../../components/ProfilePhoto/profilePhoto";
import style from "../home/App.module.scss";
import infoStyles from "./information.module.scss";
import { Select } from "../../components/Select/select";
import { validateCPF, validatePhone } from "../../utils/validators";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { getCurrentUser, getUserLogs, updateCurrentUser, updateUserPhoto } from "../../redux/user/user-thunks";
import { useEffect, useState } from "react";
import { REAUTH_MESSAGES, ReauthService } from "../../services/ReauthService";
import { ModalDefault } from "../../components/ModalDefault/modal";
import Logs from "../../components/Logs";
import { toast } from "react-toastify";
import { UpdateUser } from "../../DTOs/UpdateUser";
import Button, { ButtonColors, ButtonTypes } from "../../components/Button";
import LoadingWrapper from "../../components/Loading";
import { InputDate } from "../../components/InputDate/inputDate";
import { UserService } from "../../services/UsersService";

const SENSITIVE_FORM_FIELDS = ["email", "recoveryEmail", "identificationNumber", "phone"];

const PROFILE_COMPARABLE_FIELDS = [
  "name",
  "gender",
  "phone",
  "identificationNumber",
  "internalCode",
  "birthdate",
  "email",
] as const;

function normalizeProfileField(field: string, value: unknown): string | null {
  if (value == null || value === "") {
    return null;
  }

  const raw = String(value).trim();
  if (!raw) {
    return null;
  }

  if (field === "phone" || field === "identificationNumber") {
    const digits = raw.replace(/[^a-zA-Z0-9]/g, "");
    return digits || null;
  }

  if (field === "birthdate") {
    return raw.split("T")[0];
  }

  return raw;
}

function getChangedProfileFields(
  data: Record<string, unknown>,
  original: Record<string, unknown> | null | undefined,
): string[] {
  if (!original) {
    return [];
  }

  return PROFILE_COMPARABLE_FIELDS.filter((field) => {
    return (
      normalizeProfileField(field, data[field]) !==
      normalizeProfileField(field, original[field])
    );
  });
}

function Information() {
  const dispatch = useAppDispatch();
  const user = useSelector((state: any) => state.userReducer.currentUser);
  const updateLoading = useSelector((state: any) => state.userReducer.updateCurrentUserLoading);
  const error = useSelector((state: any) => state.userReducer.error);
  let isloading: boolean = useAppSelector(
    (state: any) => state.userReducer.loading
  );

  const [recoveryLinkSending, setRecoveryLinkSending] = useState(false);

  const form = useForm({ defaultValues: user, delayError: 700 })

  if (user && user.id && form.getValues().id !== user.id) {
    form.reset(user)
  }
  const { handleSubmit } = form;

  const submitProfile = async (data: any) => {
    const changedFields = getChangedProfileFields(data, user);

    if (changedFields.length === 0) {
      toast.info("Nenhuma alteração para salvar.");
      return;
    }

    const touchesSensitive = changedFields.some((field) =>
      SENSITIVE_FORM_FIELDS.includes(field)
    );

    const isFresh = touchesSensitive
      ? await ReauthService.isAuthFreshForSensitive()
      : await ReauthService.isAuthFreshForEdit();

    if (!isFresh) {
      return ReauthService.triggerReauth(
        {
          action: "update-profile",
          tab: "tab1",
        },
        touchesSensitive
          ? REAUTH_MESSAGES.REAUTH_REQUIRED
          : REAUTH_MESSAGES.LOGIN_REQUIRED
      );
    }

    data.phone = data.phone ? '+' + data.phone.split(' ').join('').split('-').join('').split('(').join('').split(')').join('').split('+').join('') : null;

    const { recoveryEmail: _omitRecovery, ...profileData } = data;
    const payload: any = new UpdateUser(profileData);
    Object.keys(payload).forEach((key) => {
      if (key !== "id" && !changedFields.includes(key)) {
        delete payload[key];
      }
    });

    dispatch(updateCurrentUser(payload))
      .then((resp: any) => {
        if(resp && resp.error) {
          return toast.error("Ocorreu um erro ao atualizar o usuário")
        }

        dispatch(getCurrentUser())
        toast.success("Usuário atualizado com sucesso!")
      })
  }

  const onSubmit = async (data: any) => {
    return submitProfile(data);
  }

  useEffect(() => {
    if (ReauthService.consumePendingState("update-profile")) {
      toast.info("Identidade confirmada. Revise suas alterações e clique em Salvar.");
    }
  }, []);

  const handleRequestRecoveryEmailChange = () => {
    setRecoveryLinkSending(true);
    UserService.requestRecoveryEmailChangeLink()
      .then((res) => {
        const msg =
          res.data?.message ??
          "Enviamos um link para o seu e-mail cadastrado. Verifique sua caixa de entrada.";
        toast.success(msg);
      })
      .catch(() =>
        toast.error(
          "Não foi possível enviar o link. Tente novamente mais tarde.",
        ),
      )
      .finally(() => setRecoveryLinkSending(false));
  }; 

  const genderValues = [
    { value: "M", label: "Masculino" },
    { value: "F", label: "Feminino" },
    { value: "O", label: "Outros" }
  ]

  const useModal = () => {
    const [isShown, setIsShown] = useState<boolean>(false);
    const toggle = () => {
      setIsShown(!isShown)
    };
    return {
      isShown,
      toggle,
    };
  };

  const { isShown, toggle } = useModal();

  return (
    <Wrapper>
      <LoadingWrapper isLoading={isloading || (!isloading && !user && !error)} >
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Row sm={12}>
              <Col md={2} sm={3}>
                <label>Foto</label>
                <hr className={style.line} />
                <div className="d-flex justify-content-center align-items-center">
                  <ProfilePhoto id={"user-2"} currentProfilePhotoLink={user && user.photoLink ? user.photoLink : ""}
                    onProfilePhotoChange={async function (profilePhotoData: FormData): Promise<void> {
                      if (!(await ReauthService.isAuthFreshForEdit())) {
                        return ReauthService.triggerReauth(
                          { action: "retry", tab: "tab1" },
                          REAUTH_MESSAGES.LOGIN_REQUIRED
                        );
                      }

                      dispatch(updateUserPhoto({ user: user.id, picture: profilePhotoData }))
                        .then(() => {
                          toast.success("Foto atualizada com sucesso!")
                        })
                    }} />
                </div>
              </Col>
              <Col md={10} sm={9}>
                <label>Informações</label>
                <hr className={style.line} />
                <Row>
                  <Col md={6}>
                    <Input inputName={"email"} label={"E-mail"} form={form} validators={{ required: true }} disabled={true} />
                  </Col>
                </Row>
                <Row>
                  <Col
                    md={8}
                    xl={7}
                    className={`d-flex flex-wrap gap-2 ${infoStyles.recoveryEmailRow}`}
                  >
                    <div className="flex-grow-1" style={{ minWidth: "220px" }}>
                      <Input
                        inputName={"recoveryEmail"}
                        label={"E-mail de recuperação"}
                        form={form}
                        disabled={true}
                      />
                    </div>
                    <div className="flex-shrink-0 pb-2 d-flex align-items-end">
                      <Button
                        text={recoveryLinkSending ? "Enviando…" : "Alterar"}
                        type={ButtonTypes.button}
                        color={ButtonColors.primary}
                        disabled={recoveryLinkSending}
                        onClick={handleRequestRecoveryEmailChange}
                        className={infoStyles.alterRecoveryEmailBtn}
                      />
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Input inputName={"name"} label={"Nome"} form={form} validators={{ required: true }} />
                  </Col>
                </Row>
                <Row>
                  <Col md={3}>
                    <Input inputName={"identificationNumber"} label={"CPF"} form={form} mask="999.999.999-99" validators={{ validate: validateCPF }}></Input>
                  </Col>
                  <Col md={3}>
                    <Input inputName={"phone"} label={"Telefone"} mask="+99 (99) 99999-9999" form={form} validators={{ validate: validatePhone(12) }}></Input>
                  </Col>
                </Row>
                <Row>
                  <Col md={3}>
                    <Select inputName={"gender"} label={"Gênero"} form={form} values={genderValues}></Select>
                  </Col>
                  <Col md={3}>
                    <InputDate inputName={"birthdate"} label={"Data de nascimento"} form={form} />
                  </Col>
                </Row>
                <Row>
                <Col md={3}>
                    <Input inputName={"internalCode"} label={"Código Interno"} form={form}></Input>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <div className="d-flex justify-content-end mt-3">
                      <div className="mx-2">
                        <Button text={"Logs de alteração"} type={ButtonTypes.button} onClick={toggle} color={ButtonColors.warn} disabled={updateLoading} />
                      </div>
                      <Button text={"Salvar"} type={ButtonTypes.submit} isLoading={updateLoading} />
                    </div>
                  </Col>
                </Row>
              </Col>
            </Row>

            <ModalDefault
              isShown={isShown}
              hide={toggle}
              headerText='Histórico de Alteração'
              modalContent={
                <Logs userId={user?.id} />
              }
            />

          </Form>
      </LoadingWrapper>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  /* width: 80%;
	margin: auto; */
`;

export default Information;
